// Import necessary dependencies and components
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import usePrintStore from '../../store/printStore';
import useDocumentGroupsStore from '../../store/documentGroupsStore';
import { FiFileText } from 'react-icons/fi';
import IconWrapper from './IconWrapper';
import { uploadPDF } from '../../lib/supabase/storage/upload-pdf';
import { calculateEffectivePageCount } from '../../utils/pageUtils';
import { trimPdfByPageSelection } from '../../services/pdfTrimService';
import { groupDocumentsBySettings, processMergeGroups } from '../../services/pdfMergeService';
import { fetchBWPrice, fetchColorPrice, loadShopPricing } from '../../utils/priceCalculations';
import { calculateBatchPricing, PricingGroup } from '../../lib/supabase/api/calculate-batch-pricing';
import { useGroupCalculation } from '../../hooks/useGroupCalculation';
import OrderSummaryDisplay from './OrderSummaryDisplay';
import DocumentListItem from './DocumentListItem';
import { DocumentGroup, UploadSummary } from '../../types/documentGroupTypes';
import { DocumentSource } from '../../types/documentTypes';
import { v4 as uuidv4 } from 'uuid';
import { uploadPrintJobs } from '../../services/uploadPrintJobsService';
import { useAuthStore } from '../../store/authStore';
import RazorPayService from '../../services/RazorPayService';
import { useWalletStore } from '../../store/walletStore';
import DocumentProcessingService from '../../services/DocumentProcessingService';
import { fetchWalletBalance } from '../../lib/supabase/api/fetch-wallet-balance';
import { usePaymentTypeStore } from '../../store/paymentTypeStore';
import { deleteFile } from '../../utils/indexedDBUtils';
import { FILE_STORAGE_PREFIX } from '../../utils/fileStorage';
import InsufficientWalletBalanceService from '../../services/InsufficientWalletBalanceService';
import { toast } from 'react-hot-toast';
import FullScreenLoader from '../ui/FullScreenLoader';
import { useSpecialUserStore } from '../../store/specialUserStore';
import orderProgressService from '../../services/OrderProgressService';

// Interface definitions for type safety
interface TrimmedDocument {
  id: string;
  name: string;
  file: File;
  source?: DocumentSource; // Use the proper DocumentSource type
}

interface PrintDocument {
  id: string;
  name: string;
  [key: string]: any;
}

interface PrintSettings {
  color: boolean;
  copies: number;
  doubleSided: boolean;
  effectivePageCount: number;
  orientation: 'portrait' | 'landscape';
  pageSelection: { type: string;[key: string]: any };
  paperType: string;
  [key: string]: any;
}

interface PrintStoreState {
  currentStep: number;
  documents: PrintDocument[];
  printSettings: {
    [documentId: string]: PrintSettings;
  };
  selectedDocumentId: string;
  selectedShop: string;
}

interface PrintStorage {
  state: PrintStoreState;
  version: number;
}

interface PaymentResult {
  success: boolean;
  error?: string;
  payment_id?: string;
  order_id?: string;
  amount?: number;
  updatedFiles: Array<{
    emailInvoicePath: string;
    combined_file?: string;
    groupId?: string;
  }>;
}

// Add the new prop to the component interface
interface OrderSummaryCardProps {
  isDirectShop?: boolean;
}

const OrderSummaryCard: React.FC<OrderSummaryCardProps> = ({ isDirectShop = false }) => {
  // Hook into stores and get necessary state/functions
  const { user, session, isSpecialUser, setPreviousPrintStep, signInWithGoogle } = useAuthStore();
  const { documents, printSettings, setCurrentStep, updateEffectivePageCount, removeDocument } = usePrintStore();
  const {
    documentGroups,
    addGroup,
    clearGroups,
    generatePrintSettingsCode,
    setuploadFilePath
  } = useDocumentGroupsStore();
  const { balance, handleDeductMoney } = useWalletStore();
  const { setPaymentType } = usePaymentTypeStore();
  const { handleSpecialUserOrder } = useSpecialUserStore();
  const navigate = useNavigate();

  // Local state management
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [uploadSummary, setUploadSummary] = useState<UploadSummary | null>(null);
  const [databaseUploadComplete, setDatabaseUploadComplete] = useState(false);
  const [showWalletConfirm, setShowWalletConfirm] = useState(false);
  const [isProcessingDocuments, setIsProcessingDocuments] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [progressMessage, setProgressMessage] = useState<string>('');
  const [pricingLoaded, setPricingLoaded] = useState<number>(0); // Counter to trigger re-render

  // Create a human-readable summary of print settings
  const createSettingsSummary = (settings: any) => {
    const colorMode = settings.color ? 'Color' : 'B&W';
    const duplex = settings.doubleSided ? 'Double-sided' : 'Single-sided';
    const copies = settings.copies > 1 ? `${settings.copies} copies` : '1 copy';
    const paperInfo = `${settings.paperType}, ${settings.orientation}`;

    return `${colorMode}, ${duplex}, ${paperInfo}, ${copies}`;
  };

  // Use custom hook for group calculation
  const { previewGroups, isCalculating } = useGroupCalculation({
    documents,
    printSettings,
    pricingLoaded,
    createSettingsSummary
  });

  // Fallback: Load shop pricing if not already cached (should rarely trigger since pricing is set on shop selection)
  useEffect(() => {
    const selectedShop = usePrintStore.getState().selectedShop;
    if (selectedShop && documents.length > 0) {
      // Get all unique paper types from all documents
      const paperTypes = new Set<string>();
      documents.forEach(doc => {
        const paperType = printSettings[doc.id]?.paperType || 'A4';
        paperTypes.add(paperType);
      });
      
      // Load pricing for all paper types used (will be instant if already cached from shop selection)
      Promise.all(
        Array.from(paperTypes).map(paperType => loadShopPricing(paperType))
      ).then(() => {
        setPricingLoaded(prev => prev + 1); // Trigger re-render
      }).catch(err => console.error('Failed to load shop pricing:', err));
    }
  }, [documents, printSettings]);

  // Calculate effective page counts on document changes
  useEffect(() => {
    documents.forEach(doc => {
      if (!printSettings[doc.id]?.effectivePageCount) {
        updateEffectivePageCount(doc.id);
      }
    });
  }, [documents, printSettings, updateEffectivePageCount]);

  // Cleanup groups on unmount
  useEffect(() => {
    return () => {
      clearGroups();
    };
  }, [clearGroups]);

  // Helper function to calculate price for a single document
  const calculatePrice = (docId: string) => {
    const settings = printSettings[docId];
    if (!settings) return 0;

    let pageCount = settings.effectivePageCount || 1;
    if (!settings.effectivePageCount && settings.pageSelection) {
      const doc = documents.find(d => d.id === docId);
      if (doc && doc.pageCount) {
        pageCount = calculateEffectivePageCount(settings.pageSelection, doc.pageCount);
      }
    }
    const copies = settings.copies || 1;
    const paperType = settings.paperType || 'A4'; // Get paper type from settings

    const pricePerPage = settings.color
      ? fetchColorPrice(paperType)
      : fetchBWPrice(paperType); 

    return pricePerPage * pageCount * copies;
  };

  // Calculate total pages by type (color and B&W)
  const totalPageCounts = documents.reduce((counts, doc) => {
    const settings = printSettings[doc.id];
    if (!settings) return counts;

    const pageCount = settings.effectivePageCount || 1;
    const copies = settings.copies || 1;

    if (settings.color) {
      counts.color += pageCount * copies;
    } else {
      counts.bw += pageCount * copies;
    }

    return counts;
  }, { color: 0, bw: 0 });

  // Calculate costs for each file and total price (recalculate when pricing loads)
  // Note: This individual pricing is kept for backward compatibility but tiered pricing from previewGroups is used for payments
  const fileCosts = useMemo(() => 
    documents.map(doc => calculatePrice(doc.id)),
    [documents, printSettings, pricingLoaded]
  );
  
  const totalPrice = useMemo(() => 
    fileCosts.reduce((sum, cost) => sum + cost, 0),
    [fileCosts]
  );

  // Calculate total pages for a group of documents
  const calculateGroupPageCount = (groupDocIds: string[], trimmedDocuments: TrimmedDocument[], printSettings: any) => {
    let totalPages = 0;

    for (const docId of groupDocIds) {
      const settings = printSettings[docId];
      if (!settings) continue;

      const pageCount = settings.effectivePageCount || 1;
      const copies = settings.copies || 1;
      totalPages += pageCount * copies;
    }

    return totalPages;
  };

  // Calculate total price for a group of documents
  const calculateGroupPrice = (groupDocIds: string[], printSettings: any) => {
    let groupPrice = 0;

    for (const docId of groupDocIds) {
        const settings = printSettings[docId];
        if (!settings) continue;

        const pageCount = settings.effectivePageCount || 1;
        const copies = settings.copies || 1;
        const paperType = settings.paperType || 'A4'; // Get paper type from settings
        // Match the same calculation method as calculatePrice
        const pricePerPage = settings.color 
            ? fetchColorPrice(paperType) 
            : fetchBWPrice(paperType);

        groupPrice += pricePerPage * pageCount * copies;
    }

    return groupPrice;
  };

  const handleRazorpayClick = () => {
    setPaymentType('print_order');
    handleProceedToPayment();
  };

  const handleWalletClick = () => {
    setPaymentType('wallet');
    setShowWalletConfirm(true);
  };

  const handleConfirmOrderClick = () => {
    setPaymentType('special_user');
    handleProceedToPayment();
  };

  const handleUploadPrintJobs = async (updatedFiles?: Array<{emailInvoicePath: string; combined_file?: string; groupId?: string}>, emailInvoicePath?: string, combinedFilePath?: string) => {
    try {
      const tieredTotal = previewGroups.reduce((sum, g) => sum + g.price, 0);
      const result = await uploadPrintJobs(tieredTotal, emailInvoicePath, combinedFilePath, updatedFiles);
      if (result.success) {
        console.log('All print jobs uploaded to database successfully:', result);
        setDatabaseUploadComplete(true);
        setIsComplete(true);
        toast.success(`All ${result.totalUploaded} print jobs have been submitted successfully!`);
      } else {
        console.error('Some print jobs failed to upload:', result.failedUploads);
        if (result.totalUploaded > 0) {
          toast.error(`${result.totalUploaded} print jobs uploaded, but ${result.failedUploads.length} failed. Please try again later.`);
        } else {
          throw new Error('Failed to upload any print jobs to the database');
        }
      }
    } catch (dbError) {
      console.error('Error uploading to database:', dbError);
      setUploadError(`Print job submission failed: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' | 'loading') => {
    const duration = Math.max(2000, Math.min(message.length * 100, 8000));
    return toast[type](message, {
      duration,
      position: 'top-right',
      style: {
        background: '#fff',
        color: '#333',
        border: `2px solid ${type === 'success' ? '#22c55e' : type === 'error' ? '#ef4444' : '#2563eb'}`
      },
    });
  };

  const handleProceedToPayment = async () => {
    if (isComplete) return;

    setIsProcessingDocuments(true);
    setUploadError(null);
    clearGroups();

    // Create order progress record immediately
    let progressId: string | null = null;
    
    try {
      setProgressMessage('Starting your order...');
      
      // Create order progress record for tracking
      progressId = await orderProgressService.createOrderProgress({
        totalDocuments: documents.length,
        estimatedCost: totalPrice,
        selectedShop: usePrintStore.getState().selectedShop,
        startedAt: new Date().toISOString()
      });
      console.log('Order progress created with ID:', progressId);

      setProgressMessage('Preparing your documents for printing...');
      await orderProgressService.markDocumentUploading(progressId);
      
      console.log(`Documents to upload: ${documents.length}`, documents);

      const trimmedDocuments: TrimmedDocument[] = [];
      for (const doc of documents) {
        console.log(`Processing document: ${doc.name} (ID: ${doc.id})`);

        let file: File | null = null;

        if (doc.file) {
          console.log(`File found directly in document object`);
          file = doc.file;
        } else {
          console.log(`File not found in document, trying document file map...`);
          file = await usePrintStore.getState().getDocumentFile(doc.id);

          if (!file) {
            console.error(`Could not find file data for document: ${doc.name}`);
            throw new Error(`Could not find file data for document: ${doc.name}`);
          }
        }

        const settings = printSettings[doc.id];
        let trimmedFile = file;

        if (settings && (settings.pageSelection || settings.pageRange)) {
          console.log(`Trimming PDF based on page selection...`);
          const pageSelectionString = settings.pageSelection || settings.pageRange || '';

          try {
            trimmedFile = await trimPdfByPageSelection(file, pageSelectionString);
            console.log(`PDF trimmed successfully. New size: ${trimmedFile.size}`);
          } catch (trimError) {
            console.error(`Error trimming PDF:`, trimError);
            throw new Error(`Failed to trim PDF: ${trimError instanceof Error ? trimError.message : 'Unknown error'}`);
          }
        } else {
          console.log(`No page selection specified, using entire document.`);
        }

        trimmedDocuments.push({
          id: doc.id,
          name: doc.name,
          file: trimmedFile,
          source: doc.source // Pass the source from original document
        });
      }

      console.log(`All documents trimmed: ${trimmedDocuments.length}`);
      
      setProgressMessage('Organizing your print settings...');

      // Group documents by settings (INCLUDING copies - for separate printing)
      const documentGroups = groupDocumentsBySettings(trimmedDocuments, printSettings);
      console.log(`Grouped into ${documentGroups.length} settings groups:`, documentGroups);

      const groupPrices = new Map<string, number>();
      const groupPageCounts = new Map<string, number>();
      const groupShopEarnings = new Map<string, number>(); // Store shop earnings per group
      const groupTierInfo = new Map<string, string>(); // Store tier labels

      // Step 1: Create costing groups (IGNORE copies for tier pricing calculation)
      const costingGroups: { [key: string]: { totalPages: number; groupIndices: number[] } } = {};
      
      documentGroups.forEach((group, index) => {
        const firstDocId = group.documentIds[0];
        const settings = printSettings[firstDocId];
        if (!settings) return;

        // Costing key WITHOUT copies
        const costingKey = `${settings.paperType || 'A4'}_${settings.color}_${settings.doubleSided}_${settings.orientation}`;
        
        if (!costingGroups[costingKey]) {
          costingGroups[costingKey] = { totalPages: 0, groupIndices: [] };
        }
        
        const totalGroupPages = calculateGroupPageCount(group.documentIds, trimmedDocuments, printSettings);
        costingGroups[costingKey].totalPages += totalGroupPages;
        costingGroups[costingKey].groupIndices.push(index);
        groupPageCounts.set(`group_${index}`, totalGroupPages);
      });

      console.log('[OrderSummary] Costing groups created:', Object.keys(costingGroups).length);

      // Step 2: Calculate tier pricing for costing groups (combined pages)
      const pricingGroups: PricingGroup[] = [];
      const costingKeyMap: { [key: string]: number } = {};
      
      Object.entries(costingGroups).forEach(([costingKey, data], index) => {
        const firstGroupIndex = data.groupIndices[0];
        const firstDocId = documentGroups[firstGroupIndex].documentIds[0];
        const settings = printSettings[firstDocId];
        if (!settings) return;

        const paperType = settings.paperType || 'A4';
        const isColor = settings.color || false;

        pricingGroups.push({
          group_id: `costing_${index}`,
          paper_type: paperType,
          total_pages: data.totalPages,
          is_color: isColor
        });
        
        costingKeyMap[costingKey] = index;
      });

      setProgressMessage('Calculating pricing...');
      const shopIdForPricing = usePrintStore.getState().selectedShop;
      if (!shopIdForPricing) {
        throw new Error('No shop selected');
      }

      const pricingResult = await calculateBatchPricing(shopIdForPricing, pricingGroups);
      console.log('[OrderSummary] Batch pricing result:', pricingResult);

      // Step 3: Distribute tier pricing to individual groups (respecting copies)
      Object.entries(costingGroups).forEach(([costingKey, data]) => {
        const costingIndex = costingKeyMap[costingKey];
        const tierPricing = pricingResult.groups[costingIndex];
        
        if (tierPricing.success) {
          // Apply the same cost_per_page and shop_earning_per_page to all groups in this costing group
          data.groupIndices.forEach(groupIndex => {
            const groupKey = `group_${groupIndex}`;
            const totalPages = groupPageCounts.get(groupKey) || 0;
            
            const groupPrice = totalPages * tierPricing.cost_per_page;
            const groupShopEarning = totalPages * tierPricing.shop_earning_per_page;
            
            groupPrices.set(groupKey, groupPrice);
            groupShopEarnings.set(groupKey, groupShopEarning);
            groupTierInfo.set(groupKey, tierPricing.tier_applied);
            
            console.log(`[OrderSummary] Group ${groupIndex}: ${totalPages} pages × ${tierPricing.cost_per_page} = ₹${groupPrice.toFixed(2)} (tier: ${tierPricing.tier_applied})`);
            console.log(`[OrderSummary] 🏪 Group ${groupIndex} shopEarning: ${totalPages} pages × ${tierPricing.shop_earning_per_page} = ₹${groupShopEarning.toFixed(2)}`);
          });
        } else {
          console.error(`Pricing failed for costing group ${costingKey}:`, tierPricing.error);
          // Fallback to old calculation method
          data.groupIndices.forEach(groupIndex => {
            const group = documentGroups[groupIndex];
            const groupKey = `group_${groupIndex}`;
            const fallbackPrice = calculateGroupPrice(group.documentIds, printSettings);
            groupPrices.set(groupKey, fallbackPrice);
            groupShopEarnings.set(groupKey, 0);
            groupTierInfo.set(groupKey, 'Fallback pricing');
          });
        }
      });

      documentGroups.forEach((group, index) => {
        const firstDocId = group.documentIds[0];
        const settings = printSettings[firstDocId];
        const firstDoc = trimmedDocuments.find(d => d.id === firstDocId);

        if (!settings) return;

        const settingsCode = generatePrintSettingsCode(settings);
        const settingsSummary = createSettingsSummary(settings);
        const groupKey = `group_${index}`;
        const groupPrice = groupPrices.get(groupKey) || 0;
        const groupShopEarning = groupShopEarnings.get(groupKey) || 0;
        const totalGroupPages = groupPageCounts.get(groupKey) || 0;
        const tierApplied = groupTierInfo.get(groupKey) || 'Unknown';
        const groupId = uuidv4();

        // Store with final group ID
        groupPrices.set(groupId, groupPrice);
        groupPageCounts.set(groupId, totalGroupPages);
        groupShopEarnings.set(groupId, groupShopEarning);

        const enhancedGroup: DocumentGroup = {
          id: groupId,
          documents: group.documentIds.map(id => {
            const doc = trimmedDocuments.find(d => d.id === id);
            return {
              id: id,
              name: doc?.name || 'Unknown',
              file: doc?.file as File
            };
          }),
          source: trimmedDocuments.find(d => d.id === group.documentIds[0])?.source || 'default', // Get source from first document
          printSettingsId: firstDocId,
          printSettings: settings,
          settingsSummary,
          printSettingsCode: settingsCode,
          copies: settings.copies || 1,
          totalPages: totalGroupPages,
          price: groupPrice,
          shopEarning: groupShopEarning
        };

        console.log(`[OrderSummary] ✅ Created DocumentGroup ${index}:`, {
          groupId,
          totalPages: totalGroupPages,
          price: groupPrice,
          shopEarning: groupShopEarning,
          tier: tierApplied
        });

        addGroup(enhancedGroup);
      });

      const { mergedFiles, mergeInfo } = await processMergeGroups(documentGroups);
      console.log(`After merging: ${mergedFiles.length} files to upload, ${mergeInfo.length} merge operations`);

      if (mergeInfo.length > 0) {
        const mergeMessages = mergeInfo.map(info =>
          `• ${info.docNames.join(", ")} merged with ${info.settings} settings`
        );

        const message = "Some documents with identical print settings have been merged to optimize your print job:\n\n" +
          mergeMessages.join("\n");

        console.log("Showing merge notification:", message);

        toast.success(message, {
          duration: 5000,
          position: 'top-center'
        });
      } else {
        console.log("No documents were merged - all have unique settings");
      }

      const currentGroups = useDocumentGroupsStore.getState().documentGroups;
      console.log('Preparing to upload document groups:', currentGroups);
      
      setProgressMessage('Uploading your documents securely...');

      const uploadPromises = currentGroups.map(async (group, index) => {
        const fileToUpload = mergedFiles[index] || group.documents[0].file;
        const fileName = fileToUpload.name;

        const shopEarningValue = group.shopEarning || groupShopEarnings.get(group.id) || 0;
        console.log(`[${index}] 📤 Starting upload - shopEarning check:`, {
          groupId: group.id,
          'group.shopEarning': group.shopEarning,
          'from Map': groupShopEarnings.get(group.id),
          'final value': shopEarningValue
        });

        const groupFileName = `order-${Date.now()}-${fileName}`;
        try {
          const filePath = await uploadPDF(fileToUpload, 'pdfs', groupFileName);
          console.log(`[${index}] Upload successful. filePath: ${filePath}`);

          setuploadFilePath(group.id, filePath);

          const uploadShopEarning = group.shopEarning || groupShopEarnings.get(group.id) || 0;
          console.log(`[${index}] 🎯 Creating upload result with shopEarning: ₹${uploadShopEarning}`, {
            'group.shopEarning': group.shopEarning,
            'from Map': groupShopEarnings.get(group.id),
            'final': uploadShopEarning
          });

          return {
            success: true,
            filePath,
            groupId: group.id,
            groupFileName: groupFileName,
            printSettingsCode: group.printSettingsCode,
            documentNames: group.documents.map(d => d.name),
            settingsSummary: group.settingsSummary,
            totalPages: group.totalPages || groupPageCounts.get(group.id) || 0,
            price: group.price || groupPrices.get(group.id) || 0,
            shopEarning: uploadShopEarning
          };
        } catch (uploadError) {
          console.error(`[${index}] Upload failed:`, uploadError);
          return {
            success: false,
            error: uploadError,
            documentNames: group.documents.map(d => d.name)
          };
        }
      });

      console.log('All upload promises created, waiting for resolution...');
      const results = await Promise.all(uploadPromises);

      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);

      console.log(`Upload summary: ${successful.length} successful, ${failed.length} failed`);

      if (failed.length > 0) {
        console.error('Failed uploads:', failed);
        if (progressId) {
          await orderProgressService.markFailed(progressId, `${failed.length} document(s) failed to upload`, 'document_upload');
        }
        throw new Error(`${failed.length} document(s) failed to upload`);
      }

      // Mark documents as uploaded successfully
      if (progressId) {
        await orderProgressService.markDocumentUploaded(progressId, {
          uploadedFiles: successful.map(s => ({ filePath: s.filePath, fileName: s.groupFileName })),
          totalUploaded: successful.length
        });
      }
      
      setProgressMessage('Documents uploaded successfully. Preparing payment...');

      const finalGroups = useDocumentGroupsStore.getState().documentGroups;
      
      // Create uploaded groups directly with shopEarning from the upload results
      const enrichedUploadedGroups = successful.map(s => {
        const copies = useDocumentGroupsStore.getState().documentGroups.find(g => g.id === s.groupId)?.copies || 1;
        
        console.log(`[OrderSummary] Uploaded group ${s.groupId}:`, {
          settingsSummary: s.settingsSummary,
          totalPages: s.totalPages,
          price: s.price,
          shopEarning: s.shopEarning
        });
        
        return {
          groupId: s.groupId!,
          groupFileName: s.groupFileName!,
          printSettingsCode: s.printSettingsCode!,
          documentNames: s.documentNames!,
          settingsSummary: s.settingsSummary!,
          copies: copies,
          uploadFilePath: s.filePath!,
          totalPages: s.totalPages || 0,
          price: s.price || 0,
          shopEarning: s.shopEarning || 0
        };
      });
      
      const summary: UploadSummary = {
        source: 'pdfs',
        totalGroups: finalGroups.length,
        totalDocuments: documents.length,
        totalPages: {
          color: totalPageCounts.color,
          bw: totalPageCounts.bw,
          total: totalPageCounts.color + totalPageCounts.bw
        },
        totalPrice: totalPrice,
        uploadedGroups: enrichedUploadedGroups
      };

      setUploadSummary(summary);

      console.log('=== ORDER DATA FOR DATABASE INTEGRATION ===');
      console.log('Upload Summary:', JSON.stringify(summary, null, 2));
      console.log('Document Groups:', finalGroups);
      console.log('Shop Info:', usePrintStore.getState().selectedShop);
      console.log('=== END OF ORDER DATA ===');

      console.log('Summary:', summary);

      const printStorageData = JSON.parse(localStorage.getItem('print-storage') || '{}') as PrintStorage;

      const documents_ = printStorageData.state?.documents || [];
      const printSettings_ = printStorageData.state?.printSettings || {};

      const uploadedFiles = documents_.map((doc: PrintDocument) => {
        return {
          id: doc.id,
          name: doc.name,
          settings: printSettings_[doc.id] || {
            color: false,
            copies: 1,
            doubleSided: false,
            orientation: 'portrait',
            paperType: 'A4',
            pageSelection: { type: 'all' }
          }
        };
      });
      console.log('uploadedFiles:', uploadedFiles);

      const paymentType = usePaymentTypeStore.getState().paymentType;
      
      // Update progress to payment pending
      if (progressId) {
        await orderProgressService.markPaymentPending(progressId);
      }

      if(paymentType === 'print_order') {
        setProgressMessage('Opening secure payment gateway...');
        showToast('Documents processed successfully. Opening payment gateway...', 'success');
      } else {
        setProgressMessage('Confirming your order...');
        showToast('Documents processed successfully. Confirming order...', 'success');
      }

      // Use tiered pricing total from previewGroups (same as displayed to user)
      // instead of individual document pricing to ensure consistency
      const orderAmount = previewGroups.reduce((sum, g) => sum + g.price, 0);
      const shopIdForOrder = usePrintStore.getState().selectedShop;

      if (!user) {
        throw new Error('User information is required for payment');
      }

      const userId = user.id;
      const orderSpecificData = {
        session: session?.access_token || '',
        orderSummary: [summary],
        userInfo: user.user_metadata,
        fileCosts: fileCosts,
        totalCost: orderAmount,
        shop_id: shopIdForOrder
      };

      if (paymentType === 'special_user') {
        // Create order ID
        const orderNotes = {};
        const orderData = await RazorPayService.createOrderId(
          orderAmount,
          'ORDER',
          userId,
          'INR',
          orderNotes
        );

        // Update progress with order ID
        if (progressId) {
          await orderProgressService.markPaymentInitiated(progressId, orderData.orderId);
        }

        // For special users (admin/teacher), call special order processing
        setIsProcessingPayment(true);
        setProgressMessage('Processing your complimentary order...');
        
        // Add the order ID to the orderSpecificData
        const specialUserOrderData = {
          ...orderSpecificData,
          orderId: orderData.orderId
        };
        console.log('Special user order data:', specialUserOrderData);
        const specialOrderResult = await handleSpecialUserOrder(specialUserOrderData);
        
        if (!specialOrderResult.success) {
          if (progressId) {
            await orderProgressService.markFailed(progressId, specialOrderResult.error || 'Order processing failed', 'special_order');
          }
          setUploadError('Order processing failed: ' + specialOrderResult.error);
          return;
        }
        
        // Mark payment as completed for special users
        if (progressId) {
          await orderProgressService.markPaymentCompleted(progressId, 'special_user_order');
        }
  
        // Upload print jobs with zero price for database records
        await handleUploadPrintJobs(
          specialOrderResult.updatedFiles,
          specialOrderResult.updatedFiles?.[0]?.emailInvoicePath,
          specialOrderResult.updatedFiles?.[0]?.combined_file
        );
      } 
      else if (paymentType === 'print_order') {
        setProgressMessage('Creating your secure payment order...');
        const orderNotes = {};
        const orderData = await RazorPayService.createOrderId(
          orderAmount,
          'ORDER',
          userId,
          'INR',
          orderNotes,
          orderSpecificData // Pass the order data for webhook processing
        );
        
        // Update progress with order ID
        if (progressId) {
          await orderProgressService.markPaymentInitiated(progressId, orderData.orderId);
        }
        
        const paymentResult = await RazorPayService.processOrderPayment(
          orderData.orderId,
          orderAmount,
          userId,
          {
            prefill: {
              name: user?.user_metadata.name,
              email: user?.user_metadata.email,
              contact: user?.phone || '',
              shop_id: shopIdForOrder
            }
          },
          orderSpecificData
        ) as PaymentResult;

        if (!paymentResult.success) {
          if (progressId) {
            await orderProgressService.markFailed(progressId, paymentResult.error || 'Payment failed', 'razorpay_payment');
          }
          setUploadError('Payment failed: ' + paymentResult.error);
          return;
        }
        
        // Mark payment as completed
        if (progressId) {
          await orderProgressService.markPaymentCompleted(progressId, paymentResult.payment_id || '');
        }
        setProgressMessage('Payment successful. Finalizing your order...');

        const emailInvoicePath = paymentResult.updatedFiles?.[0]?.emailInvoicePath;
        const combinedFilePath = paymentResult.updatedFiles?.[0]?.combined_file;
        console.log('Email invoice path:', emailInvoicePath);
        console.log('Combined file path:', combinedFilePath);
      } else if (paymentType === 'wallet') {
        setProgressMessage('Processing wallet payment...');
        const walletBalance = await fetchWalletBalance(userId);
        if (!walletBalance) {
          toast.error('Unable to fetch wallet balance');
          return;
        }

        if (walletBalance === 0) {
          // Direct Razorpay payment if wallet is empty
          setProgressMessage('Wallet empty. Redirecting to payment gateway...');
          const orderNotes = {};
          const orderData = await RazorPayService.createOrderId(
            orderAmount,
            'ORDER',
            userId,
            'INR',
            orderNotes,
            orderSpecificData // Pass the order data for webhook processing
          );

          const paymentResult = await RazorPayService.processOrderPayment(
            orderData.orderId,
            orderAmount,
            userId,
            {
              prefill: {
                name: user?.user_metadata.name,
                email: user?.user_metadata.email,
                contact: user?.phone || '',
                shop_id: shopIdForOrder
              }
            },
            orderSpecificData
          ) as PaymentResult;

          if (!paymentResult.success) {
            if (progressId) {
              await orderProgressService.markFailed(progressId, paymentResult.error || 'Payment failed', 'wallet_razorpay_payment');
            }
            setUploadError('Payment failed: ' + paymentResult.error);
            return;
          }
          
          // Mark payment as completed
          if (progressId) {
            await orderProgressService.markPaymentCompleted(progressId, paymentResult.payment_id || '');
          }
          setProgressMessage('Payment successful. Finalizing your order...');

          const emailInvoicePath = paymentResult.updatedFiles?.[0]?.emailInvoicePath;
          const combinedFilePath = paymentResult.updatedFiles?.[0]?.combined_file;
          console.log('Email invoice path:', emailInvoicePath);
          console.log('Combined file path:', combinedFilePath);
          //await handleUploadPrintJobs(paymentResult.updatedFiles, emailInvoicePath, combinedFilePath);

        } else if (walletBalance >= orderAmount) {
          setProgressMessage('Processing wallet payment...');
          // Full wallet payment
          console.log('orderSpecificData:', orderSpecificData);
          const success = await handleDeductMoney(orderAmount, orderSpecificData);
          if (!success || !success.success) {
            if (progressId) {
              await orderProgressService.markFailed(progressId, 'Wallet payment failed', 'wallet_payment');
            }
            setUploadError('Wallet payment failed');
            return;
          }
          
          // Mark payment as completed
          if (progressId) {
            await orderProgressService.markPaymentCompleted(progressId, 'wallet_payment');
          }
          setProgressMessage('Payment successful!');
          
          console.log('Wallet payment successful:', success);
          // Combined PDFs generated, jobs submitted to backend, email invoice sent in background

        } else {
          // Split payment
          const splitPaymentResult = await InsufficientWalletBalanceService.handleSplitPayment(
            userId,
            '',  // No doc id for print orders
            orderAmount,
            walletBalance,
            user.user_metadata,
            'ORDER',
            orderSpecificData
          );

          if (!splitPaymentResult.success) {
            if (progressId) {
              await orderProgressService.markFailed(progressId, 'Split payment failed', 'split_payment');
            }
            setUploadError('Split payment failed');
            return;
          }
          
          // Mark payment as completed
          if (progressId) {
            await orderProgressService.markPaymentCompleted(progressId, 'split_payment');
          }
          setProgressMessage('Payment successful. Finalizing your order...');
          
          const emailInvoicePath = splitPaymentResult.updatedFiles?.[0]?.emailInvoicePath;
          const combinedFilePath = splitPaymentResult.updatedFiles?.[0]?.combined_file;
          console.log('Email invoice path:', emailInvoicePath);
          console.log('Combined file path:', combinedFilePath);
          // await handleUploadPrintJobs(splitPaymentResult.updatedFiles, emailInvoicePath, combinedFilePath);
        }
      }

      try {
        console.log('Starting database upload...');
        setProgressMessage('Submitting your print jobs...');
        
        // Mark as processing
        if (progressId) {
          await orderProgressService.markProcessing(progressId);
        }
        
        const paymentType = usePaymentTypeStore.getState().paymentType;
        
        // For wallet payments, jobs are already submitted - just mark as complete
        if (paymentType === 'wallet') {
          console.log('Wallet payment - jobs already submitted');
          setDatabaseUploadComplete(true);
          setIsComplete(true);
          
          if (progressId) {
            await orderProgressService.markCompleted(progressId);
          }
          
          setProgressMessage('Order completed successfully.');
          showToast('Your print order has been confirmed! Invoice will be sent shortly.', 'success');
        } else {
          // For other payment types, use the existing flow
          const result = { success: true };

          if (result.success) {
            console.log('All print jobs uploaded to database successfully:', result);
            setDatabaseUploadComplete(true);
            setIsComplete(true);
            
            // Mark order as completed
            if (progressId) {
              await orderProgressService.markCompleted(progressId);
            }
            
            setProgressMessage('Order completed successfully.');
            showToast('Your print jobs have been submitted successfully.', 'success');
          } else {
            console.error('Some print jobs failed to upload:');
            if (progressId) {
              await orderProgressService.markFailed(progressId, 'Failed to submit print jobs', 'database_upload');
            }
          }
        }
      } catch (dbError) {
        console.error('Error uploading to database:', dbError);
        const errorMsg = dbError instanceof Error ? dbError.message : 'Unknown error';
        if (progressId) {
          await orderProgressService.markFailed(progressId, `Print job submission failed: ${errorMsg}`, 'database_upload');
        }
        setUploadError(`Print job submission failed: ${errorMsg}`);
      }

      if (!uploadError) {
        showToast('Order confirmed successfully.', 'success');
      }
      } catch (error) {
        console.error('Error in upload process:', error);

        const errorMessage = error instanceof Error 
          ? `We encountered an issue: ${error.message}. Please try again.` 
          : 'Something went wrong. Please try again.';
        
        // Mark progress as failed if we have a progress ID
        if (progressId) {
          try {
            await orderProgressService.markFailed(progressId, errorMessage, 'general_error');
          } catch (progressError) {
            console.error('Failed to update progress status:', progressError);
          }
        }
        
        setUploadError(errorMessage);
        setProgressMessage('');
        showToast(errorMessage, 'error');
    } finally {
      setIsProcessingDocuments(false);
      setIsProcessingPayment(false);
      setIsUploading(false);
      console.log('=== Document upload process completed ===');

      // Cleanup after success
      // 1. Clear IndexedDB files
      for (const doc of documents) {
        const key = `${FILE_STORAGE_PREFIX}${doc.id}`;
        await deleteFile(key);
        console.log('Cleared IndexedDB file:', key);
      }
      // 2. Remove documents from print store
      for (const doc of documents) {
        removeDocument(doc.id);
        console.log('Removed document from store:', doc.id);
      }
      // 3. Reset current step and navigate
      setCurrentStep(0);
      console.log('Reset current step to 0');
      navigate('/my-account?tab=orders');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

 // Handle sign in button click
 const handleSignInClick = () => {
  // Save current step before redirecting to login
  setPreviousPrintStep(usePrintStore.getState().currentStep);
  // Use the Google OAuth flow from auth store
  signInWithGoogle();
};

  // Render payment buttons based on user type
  const renderPaymentButtons = () => {
    // Show Sign In button when user is not logged in
    if (!user) {
      return (
        <button
          onClick={handleSignInClick}
          className="w-full sm:w-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          Sign In to Continue
        </button>
      );
    }
    
    // For special users (admin or teacher), show only "Confirm Order" button
    if (isSpecialUser()) {
      return (
        <button
          onClick={handleConfirmOrderClick}
          className={`w-full sm:w-auto px-6 py-3 rounded-lg transition-colors flex items-center justify-center gap-2 ${
            isProcessingDocuments || isProcessingPayment || isComplete || isCalculating
              ? 'bg-gray-400 cursor-not-allowed text-gray-200'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
          disabled={isProcessingDocuments || isProcessingPayment || isComplete || isCalculating}
        >
          {isProcessingPayment ? 'Processing...' : isComplete ? 'Completed' : 'Confirm Order'}
        </button>
      );
    } 
    
    // For regular logged-in users, show wallet and Razorpay buttons
    return (
      <>
        <button
          onClick={handleWalletClick}
          className={`w-full sm:w-auto px-6 py-3 rounded-lg border-2 transition-colors flex items-center justify-center gap-2 ${
            balance !== null && balance < previewGroups.reduce((sum, g) => sum + g.price, 0) || isCalculating
              ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
              : 'bg-white hover:bg-blue-50 text-blue-600 border-blue-600'
          }`}
          disabled={isProcessingDocuments || isProcessingPayment || isComplete || (balance !== null && balance < previewGroups.reduce((sum, g) => sum + g.price, 0)) || isCalculating}
        >
          {isProcessingPayment ? 'Processing...' : 'Pay via Wallet'}
        </button>
        <button
          onClick={handleRazorpayClick}
          className={`w-full sm:w-auto px-6 py-3 rounded-lg transition-colors flex items-center justify-center gap-2 ${
            isProcessingDocuments || isProcessingPayment || isComplete || isCalculating
              ? 'bg-gray-400 cursor-not-allowed text-gray-200'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
          disabled={isProcessingDocuments || isProcessingPayment || isComplete || isCalculating}
        >
          {isProcessingPayment ? 'Processing...' : isComplete ? 'Completed' : `Pay ₹${previewGroups.reduce((sum, g) => sum + g.price, 0).toFixed(2)}`}
        </button>
      </>
    );
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md w-full max-w-2xl mx-auto">
      {/* Show full-screen loader when processing */}
      {isProcessingDocuments && <FullScreenLoader message={progressMessage || "Processing your documents..."} />}
      {isProcessingPayment && <FullScreenLoader message={progressMessage || "Processing your payment..."} />}
      
      <div className="flex items-center mb-6">
        <IconWrapper icon={FiFileText} className="text-blue-600 w-8 h-8" />
        <h2 className="text-2xl font-semibold text-blue-600 ml-3">Order Summary</h2>
      </div>

      <OrderSummaryDisplay
        previewGroups={previewGroups}
        totalPageCounts={totalPageCounts}
        uploadError={uploadError}
        isCalculating={isCalculating}
      />

      <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-4 mt-8">
        <button
          onClick={() => {
            setCurrentStep(isDirectShop ? 1 : 2);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="w-full sm:w-auto px-6 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          disabled={isProcessingDocuments || isProcessingPayment}
        >
          Back
        </button>
        <div className="flex flex-col sm:flex-row gap-3">
          {renderPaymentButtons()}
        </div>
      </div>

      {showWalletConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">Confirm Wallet Payment</h3>
            <div className="space-y-3">
              <div className="flex justify-between text-gray-600">
                <span>Amount to be deducted:</span>
                <span className="font-semibold">₹{previewGroups.reduce((sum, g) => sum + g.price, 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Available balance:</span>
                <span className="font-semibold">₹{balance?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-4">
                <p className="text-sm text-yellow-700">
                  ⚠️ This action cannot be undone after confirmation.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowWalletConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleProceedToPayment}
                className={`px-4 py-2 rounded transition-colors flex items-center gap-2 ${
                  isProcessingDocuments || isProcessingPayment || isCalculating
                    ? 'bg-gray-400 cursor-not-allowed text-gray-200'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
                disabled={isProcessingDocuments || isProcessingPayment || isCalculating}
              >
                {isProcessingDocuments || isProcessingPayment ? (
                  <>
                    <span>Processing...</span>
                  </>
                ) : (
                  'Confirm Order'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {isComplete && !isProcessingDocuments && !isProcessingPayment && (
        <div className={`mt-4 text-center ${databaseUploadComplete ? 'text-green-600' : 'text-blue-600'}`}>
          {databaseUploadComplete && ' Print jobs submitted successfully'}
        </div>
      )}
    </div>
  );
};

export default OrderSummaryCard;
